import { Button } from "@dropbox/dig-components/buttons";
import { UIIcon } from "@dropbox/dig-icons";
import { ChevronLeftLine, DeleteForeverLine } from "@dropbox/dig-icons/assets";
import { getWorkflowAgent } from "@mirage/service-conversation";
import { useWorkflowAgents } from "@mirage/service-conversation/hooks/useWorkflowAgents";
import i18n from "@mirage/translations";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { type ReactNode, useEffect, useState } from "react";
import { useMatch } from "react-router-dom";
import { SourcesCacheProvider } from "./SourcesCacheContext";
import { WorkflowAgentUI } from "./WorkflowAgent";

interface WorkflowAgentUIComponents {
  workflowAgentUI: ReactNode;
  backButton: ReactNode;
  deleteButton: ReactNode;
  showWorkflowAgent: boolean;
  setShowWorkflowAgent: (show: boolean) => void;
  isWorkflowAgentRoute: boolean;
  workflowAgentId?: string;
}

export function useWorkflowAgentUI({
  onResetToChat,
}: {
  onResetToChat: () => void; // Navigate away from the workflow agent
}): WorkflowAgentUIComponents | undefined {
  const [showWorkflowAgent, setShowWorkflowAgent] = useState(false);
  const isNewWorkflowAgentMatch = useMatch(RoutePath.NEW_WORKFLOW_AGENT);
  const isWorkflowAgentMatch = useMatch(RoutePath.WORKFLOW_AGENT);
  const isWorkflowAgentRoute = Boolean(
    isNewWorkflowAgentMatch || isWorkflowAgentMatch,
  );
  const workflowAgentId = isWorkflowAgentMatch
    ? isWorkflowAgentMatch.params.agentId
    : undefined;

  const showRightTitleActions = !!(
    isWorkflowAgentMatch &&
    !isNewWorkflowAgentMatch &&
    workflowAgentId
  );
  const { deleteAgent } = useWorkflowAgents();

  const handleDeleteAgent = async () => {
    if (!workflowAgentId) return;
    const agent = await getWorkflowAgent(workflowAgentId);
    await deleteAgent(agent);
    onResetToChat();
  };

  const handleBackClick = () => {
    onResetToChat();
  };

  // Set showWorkflowAgent to true automatically when on NEW_WORKFLOW_AGENT route
  useEffect(() => {
    if (isWorkflowAgentRoute && !showWorkflowAgent) {
      setShowWorkflowAgent(true);
    } else if (!isWorkflowAgentRoute && showWorkflowAgent) {
      setShowWorkflowAgent(false);
    }
  }, [isWorkflowAgentRoute, showWorkflowAgent, setShowWorkflowAgent]);

  const workflowAgentComponent = showWorkflowAgent ? (
    <SourcesCacheProvider>
      <WorkflowAgentUI
        agentId={workflowAgentId}
        onExitWorkflowAgent={onResetToChat}
      />
    </SourcesCacheProvider>
  ) : null;

  const backButtonComponent = (
    <Button variant="borderless" onClick={handleBackClick}>
      <UIIcon src={ChevronLeftLine} />
    </Button>
  );

  const deleteButtonComponent = showRightTitleActions ? (
    <Button
      variant="primary"
      tone="destructive"
      onClick={handleDeleteAgent}
      withIconStart={<UIIcon src={DeleteForeverLine} />}
    >
      {i18n.t("delete")}
    </Button>
  ) : null;

  return {
    workflowAgentUI: workflowAgentComponent,
    backButton: backButtonComponent,
    deleteButton: deleteButtonComponent,
    showWorkflowAgent,
    setShowWorkflowAgent,
    isWorkflowAgentRoute,
    workflowAgentId,
  };
}
